/* eslint-disable @typescript-eslint/ban-ts-comment */
import Close from '@mui/icons-material/Close'
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Typography,
  useMediaQuery
} from '@mui/material'
import { Loading, Button, CardHeader, IconButton, Tooltip } from 'components'
import { USER_ROLES_OPTION } from 'config'
import { useAuth } from 'lib/auth'
import {
  useApproveProposal,
  useLoadOrderDirtRoadQuizResponses,
  useLoadOrderReviewReasons
} from 'modules/orders/hooks'
import { Fragment, useMemo } from 'react'
import { useRejectProposalStore } from '../rejectProposal'
import { usePendingReviewModalStore } from './pendingReviewModalStore'
import { useTheme } from '@mui/material/styles'
import { formatCurrency } from 'utils'

const MessageReason = ({ reason }: any) => {
  if (!reason) return null

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2">{reason}</Typography>
    </Grid>
  )
}

const ItemsDiscountReason = ({ reason }: any) => {
  if (!reason) return null

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2">
        Desconto aplicado maior que o permitido:
      </Typography>
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {reason.map((item: any) => (
          <ListItem key={item.id}>
            <ListItemText
              primary={`${item.productId} - ${item.name}`}
              secondary={
                <>
                  {item?.configurationDescription && (
                    <Tooltip
                      title={item?.configurationDescription}
                      maxWidth={500}
                    >
                      <div
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        <Typography
                          variant="caption"
                          noWrap
                          color="text.primary"
                        >
                          Config: {item?.configurationDescription}
                        </Typography>
                      </div>
                    </Tooltip>
                  )}
                  <Grid container>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.primary">
                        Desc. máximo: {item.maximumDiscount}%
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.primary">
                        Pr. Formado: {formatCurrency(item.defaultPrice)}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.primary">
                        Desc. aplicado: {item.discount}%
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.primary">
                        Pr. aplicado: {formatCurrency(item.salePrice)}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              }
            />
          </ListItem>
        ))}
      </List>
    </Grid>
  )
}

const DirtRoadReason = ({ reason }: any) => {
  const proposalId = usePendingReviewModalStore.useProposalId()

  const {
    data: responses,
    isLoading: loading,
    isFetching
  } = useLoadOrderDirtRoadQuizResponses({
    proposalId
  })

  const isLoading = loading || isFetching

  if (!reason) return null

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2">
        Quantos km de estrada de chão?
      </Typography>
      {isLoading && <Skeleton width={80} animation="wave" height={30} />}
      {!isLoading && (
        <Typography variant="body2">{responses?.data?.quest1}km</Typography>
      )}
      <Typography variant="subtitle2">
        Entra carreta no local e a mesma consegue fazer o retorno?
        {isLoading && <Skeleton width={80} animation="wave" height={30} />}
        {!isLoading && (
          <Typography variant="body2">
            {responses?.data?.quest2 ? 'SIM' : 'NÃO'}
          </Typography>
        )}
      </Typography>
      <Typography variant="subtitle2">
        Em caso de chuva, o caminhão consegue chegar até o local?
        {isLoading && <Skeleton width={80} animation="wave" height={30} />}
        {!isLoading && (
          <Typography variant="body2">
            {responses?.data?.quest3 ? 'SIM' : 'NÃO'}
          </Typography>
        )}
      </Typography>
      <Typography variant="subtitle2">
        Já anexou a localização da entrega?
        {isLoading && <Skeleton width={80} animation="wave" height={30} />}
        {!isLoading && (
          <Typography variant="body2">
            {responses?.data?.quest4 ? 'SIM' : 'NÃO'}
          </Typography>
        )}
      </Typography>
    </Grid>
  )
}

const reasonsList = [
  {
    Reason: MessageReason,
    key: 'payment'
  },
  {
    Reason: MessageReason,
    key: 'delivery'
  },
  {
    Reason: MessageReason,
    key: 'minimalOrder'
  },
  {
    Reason: MessageReason,
    key: 'minimalFreight'
  },
  {
    Reason: ItemsDiscountReason,
    key: 'itemsWithDiscount'
  },
  {
    Reason: DirtRoadReason,
    key: 'dirtRoadQuiz'
  }
]

export const PendingReviewModal = () => {
  const setModalIsOpen = usePendingReviewModalStore.useSetModalIsOpen()
  const isModalOpen = usePendingReviewModalStore.useIsModalOpen()
  const proposalId = usePendingReviewModalStore.useProposalId()

  const { user } = useAuth()
  const theme = useTheme()

  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  const { data, isLoading } = useLoadOrderReviewReasons({
    proposalId: proposalId
  })

  const canApprove = data?.data?.canApprove
  const role = useMemo(
    () =>
      USER_ROLES_OPTION.find((item: any) => item.level === canApprove)?.name,
    [canApprove]
  )

  const iCanApprove = useMemo(
    () =>
      (USER_ROLES_OPTION.find((item: any) => item.id === user?.role)?.level ||
        0) >= (canApprove || 4),

    [canApprove, user?.role]
  )

  const iCanReject = useMemo(
    () =>
      canApprove === 4
        ? (USER_ROLES_OPTION.find((item: any) => item.id === user?.role)
            ?.level || 0) >= 2
        : (USER_ROLES_OPTION.find((item: any) => item.id === user?.role)
            ?.level || 0) >= (canApprove || 4),

    [canApprove, user?.role]
  )

  const setOpenRejectModal = useRejectProposalStore.useSetModalOpen()
  const setProposalId = useRejectProposalStore.useSetProposalId()

  const { mutate: approve } = useApproveProposal({
    onSuccess: () => setModalIsOpen(false)
  })

  const handleApproveProposal = () => {
    approve(proposalId)
  }

  const handleRejectProposal = () => {
    setOpenRejectModal(true)
    setProposalId(proposalId)
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      scroll="paper"
      open={isModalOpen}
      onClose={() => setModalIsOpen(false)}
      keepMounted={false}
    >
      <CardHeader
        title={`Orçamento n° ${proposalId}`}
        HeaderRight={
          <IconButton onClick={() => setModalIsOpen(false)}>
            <Close />
          </IconButton>
        }
      />
      <Divider />

      <DialogContent>
        <Grid container spacing={2}>
          {isLoading && (
            <Grid item xs={12}>
              <Loading />
            </Grid>
          )}
          {reasonsList.map((reason) => {
            return (
              <Fragment key={reason.key}>
                {/* @ts-ignore */}
                <reason.Reason reason={data?.data?.[reason.key]} />
              </Fragment>
            )
          })}
        </Grid>
      </DialogContent>
      <Divider />

      <DialogActions>
        <div
          style={{
            width: '100%'
          }}
        >
          <div
            style={{
              marginBottom: 16
            }}
          >
            <Grid
              container
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <Grid item xs={12}>
                <Typography variant="caption">
                  UF: {data?.data?.proposal?.customer?.uf}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption">
                  Nível de acesso necessário para aprovar: {role}
                </Typography>
              </Grid>
            </Grid>
          </div>

          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem'
            }}
          >
            <Button
              variant="text"
              sx={{
                width: { sm: 100, md: 200 }
              }}
              onClick={() => setModalIsOpen(false)}
            >
              Fechar
            </Button>
            <Button
              sx={{
                width: { sm: 100, md: 200 }
              }}
              type="button"
              onClick={handleRejectProposal}
              disabled={!iCanReject}
            >
              Rejeitar
            </Button>
            <Button
              sx={{
                width: { sm: 100, md: 200 }
              }}
              type="button"
              onClick={handleApproveProposal}
              disabled={!iCanApprove}
            >
              Aprovar
            </Button>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  )
}
